import { useEffect, useReducer } from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import {
  BubbleChartRenderingLayer,
  BubbleChartRenderingLayerProps,
  BubbleChartViewModelSourceData,
} from '@ardoq/bubble-chart';
import {
  buildWorkspaceModeViewModelSourceData,
  getInitialWorkspaceModeSourceDataState,
  WorkspaceModeBubbleChartSourceDataArgs,
} from './sourceData/buildWorkspaceModeViewModelSourceData';
import type { WorkspaceModeBubbleChartSettingsControlsDependencies } from './workspaceMode/settingsControls';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { getBubbleChartViewComponentIds } from './viewComponentIds';

const publishViewComponentIds = ({ data }: BubbleChartViewModelSourceData) =>
  dispatchAction(
    readViewComponentIds({
      [ViewIds.BUBBLE]: getBubbleChartViewComponentIds(data),
    })
  );

type RenderingLayerProps = Omit<
  BubbleChartRenderingLayerProps<WorkspaceModeBubbleChartSettingsControlsDependencies>,
  'sourceData'
>;

export type WorkspaceModeBubbleChartSourceDataLayerProps = RenderingLayerProps &
  WorkspaceModeBubbleChartSourceDataArgs;

const WorkspaceModeBubbleChartSourceDataLayer = ({
  viewInstanceId,
  viewSettingsForRenderingData,
  bubbleChartCommands,
  settingsControlsHandlers,
  onRenderingDataChange,
  BubbleChartComponent,
  ViewLegendComponent,
  EmptyStateComponent,
  ZoomControlsComponent,
  trackingCallbacks,
  context,
  components,
  viewSettings,
  graph,
  scopeDiffState,
  activeDiffMode,
  activeFilterState,
  permissionContext,
  locale,
  i18nLocale,
  fields,
  trackingId,
}: WorkspaceModeBubbleChartSourceDataLayerProps) => {
  const [sourceData, updateSourceData] = useReducer(
    buildWorkspaceModeViewModelSourceData,
    viewInstanceId,
    getInitialWorkspaceModeSourceDataState
  );

  useEffect(() => {
    updateSourceData({
      context,
      components,
      viewSettings,
      graph,
      scopeDiffState,
      activeDiffMode,
      activeFilterState,
      permissionContext,
      locale,
      i18nLocale,
      fields,
      trackingId,
    });
  }, [
    context,
    components,
    viewSettings,
    graph,
    scopeDiffState,
    activeDiffMode,
    activeFilterState,
    permissionContext,
    locale,
    i18nLocale,
    fields,
    trackingId,
  ]);

  useEffect(() => {
    publishViewComponentIds(sourceData);
  }, [sourceData]);

  return (
    <BubbleChartRenderingLayer<WorkspaceModeBubbleChartSettingsControlsDependencies>
      viewInstanceId={viewInstanceId}
      sourceData={sourceData}
      viewSettingsForRenderingData={viewSettingsForRenderingData}
      bubbleChartCommands={bubbleChartCommands}
      settingsControlsHandlers={settingsControlsHandlers}
      onRenderingDataChange={onRenderingDataChange}
      BubbleChartComponent={BubbleChartComponent}
      ViewLegendComponent={ViewLegendComponent}
      EmptyStateComponent={EmptyStateComponent}
      ZoomControlsComponent={ZoomControlsComponent}
      trackingCallbacks={trackingCallbacks}
    />
  );
};

export { WorkspaceModeBubbleChartSourceDataLayer };
